import React from 'react';

import { PictureProps } from '@soluto-design/picture';
import { Button, Icon, Text, Title } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import TagAuthor from '../../atoms/TagAuthor';
import { CardContentProps } from '../CardContent';

interface CardPiecesProps extends CardContentProps {
    className?: string;
    isLarge?: boolean;
    buttonTabIndex?: number;
    logo?: PictureProps;
    location?: string;
}

export function CardTop({ tag, title, className, logo }: CardPiecesProps) {
    return (
        <div className={className}>
            {!!logo?.src && (
                <span className="h-24">
                    <img
                        src={logo?.src}
                        alt={logo?.alt || title}
                        className="grayscale h-full w-auto"
                        loading="lazy"
                    />
                </span>
            )}
            {!!tag && (
                <Text
                    component="p"
                    className="text-c100 font-regular text-text-primary uppercase">
                    {tag}
                </Text>
            )}
            {!!title && (
                <Title component="h4" className="text-h400 text-text-primary">
                    {title}
                </Title>
            )}
        </div>
    );
}

export function CardBottom({
    text,
    date,
    persons,
    cta,
    className,
    isLarge,
    buttonTabIndex,
    location,
}: CardPiecesProps) {
    return (
        <div className={className}>
            <div>
                {!!text && (
                    <Text
                        isHtml
                        component="div"
                        className={cls(
                            'text-b400 text-text-secondary',
                            isLarge &&
                                '[&>p]:pt-xs [&>p:first-child]:pt-0 [&>strong]:font-semibold',
                        )}>
                        {text}
                    </Text>
                )}
                {!!(date || (persons && persons.length > 0)) && (
                    <div
                        className={cls(
                            'mt-lg w-fit flex flex-col gap-12 mb-40',
                            isLarge &&
                                !!location &&
                                'md:gap-4 md:flex-row md:items-center',
                        )}>
                        {!!date && (
                            <Text
                                component="p"
                                className={cls('text-c100 text-text-primary')}>
                                {date}
                            </Text>
                        )}
                        {persons && persons.length > 0 && (
                            <TagAuthor persons={persons} disablePopover />
                        )}
                        {location && (
                            <>
                                {isLarge && !!(date || persons?.length) && (
                                    <Icon
                                        icon="divider"
                                        className={cls(
                                            'hidden xl:block self-center text-[20px]',
                                            isLarge && 'md:block',
                                        )}
                                    />
                                )}
                                <span className="text-c100 inline-flex items-center gap-x-4">
                                    <Icon icon="location" />
                                    {location}
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>

            {cta && (
                <Button
                    variant="tertiary"
                    size="medium"
                    rightIcon="arrow-direction--type-right"
                    className="w-fit text-nowrap"
                    href={cta.href}
                    {...(cta.rel && { rel: cta.rel })}
                    {...(cta.target && { target: cta.target })}
                    tabIndex={buttonTabIndex ? buttonTabIndex : 0}>
                    {cta.label ?? cta.children}
                </Button>
            )}
        </div>
    );
}
